/**
 * @file: host.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The narrow seam the service-subject catalog screen reads from, so it can be
 * driven by either the order flow (its native host) or the reservation flow
 * (a standalone host backed by a reservation's items). It is exactly the subset
 * of `OrderFlowContext` the catalog subtree needs — the screen body consumes
 * `useServiceSubjectsHost()` instead of `useOrderFlowContext()` directly.
 */

import { OrderDetail } from '@euler/model';
import { InspectionTemplate } from '@euler/model/InspectionTemplate';
import { ServiceSubject } from '@euler/model/OrderDetail';
import { Draft } from 'immer';
import { createContext, useContext } from 'react';
import { BehaviorSubject } from 'rxjs';
import { OrderExtra, OrderPageCapabilities } from '../../types';

export interface ServiceSubjectsHost {
  // The container of `serviceSubjects` (the picked items) + the mutator over it.
  orderDetail$: BehaviorSubject<OrderExtra>;
  updateDetail: (update: (draft: Draft<OrderDetail>) => void) => void;
  // The catalog's inspection-template source.
  inspectionTemplates: InspectionTemplate[];
  // Drives the custom-service-subject sheet (true = new, a subject = edit).
  showCustomServiceSubjectModal$: BehaviorSubject<boolean | ServiceSubject>;
  // The search keyword shared by the header input and the list filter.
  serviceSubjectQuery$: BehaviorSubject<string>;
  // The order flow's per-session "stop prompting" toggle for non-inspection
  // items (set when the user opts out of the assignee picker).
  skipJobAssignment$: BehaviorSubject<boolean>;
  // Whether this context assigns technicians at all. The order flow does (false
  // is never set here); the reservation flow does NOT — items are assigned at
  // order creation, so picking any item adds it without an assignee. Treated as
  // enabled when omitted.
  jobAssignmentEnabled?: boolean;
  // Read-only subject sets (order edit mode); absent for reservations.
  capabilities?: OrderPageCapabilities;
  // The vehicle the catalog scopes its pending-issues lookup to.
  vehicle: { licensePlateNo?: string; vin?: string };
  // Post-edit validation hook (the order flow validates its form field).
  validateServiceSubjects?: () => void;
}

const ServiceSubjectsHostContext = createContext<ServiceSubjectsHost>(
  null as unknown as ServiceSubjectsHost,
);

export const ServiceSubjectsHostProvider = ServiceSubjectsHostContext.Provider;

export const useServiceSubjectsHost = (): ServiceSubjectsHost =>
  useContext(ServiceSubjectsHostContext);
